import React, { useState, useEffect } from 'react';
import './InstallAppPrompt.css';
import { tr } from './i18n';

/**
 * Custom "Install App" banner for the phone/PWA experience.
 *
 * Chrome/Edge/Android WebView fire a `beforeinstallprompt` event once the
 * site qualifies (valid manifest.json + registered service worker + HTTPS).
 * We intercept that event, stash it, and show our own branded banner instead
 * of relying on the browser's own mini-infobar - this lets us control WHEN
 * it shows (e.g. not on the very first paint) and match the app's style.
 *
 * iOS Safari never fires beforeinstallprompt - there is no programmatic
 * install API there - so we detect iOS separately and show short manual
 * instructions ("Tap Share, then Add to Home Screen") instead.
 *
 * Drop <InstallAppPrompt /> once near the root of the app (e.g. in App.js,
 * alongside <Footer />) and it handles everything itself, including not
 * re-nagging someone who already dismissed it or already has it installed.
 */

const DISMISS_KEY = 'ak4tek_install_prompt_dismissed_at';
// Don't show the banner again for this many days after a dismissal.
const DISMISS_COOLDOWN_DAYS = 14;

const isIos = () =>
  /iphone|ipad|ipod/i.test(window.navigator.userAgent) &&
  !window.MSStream;

const isInStandaloneMode = () =>
  // Installed PWA on iOS
  window.navigator.standalone === true ||
  // Installed PWA on Android/desktop Chrome, Edge, etc.
  window.matchMedia('(display-mode: standalone)').matches;

const wasRecentlyDismissed = () => {
  const raw = localStorage.getItem(DISMISS_KEY);
  if (!raw) return false;
  const dismissedAt = Number(raw);
  if (!dismissedAt) return false;
  const days = (Date.now() - dismissedAt) / (1000 * 60 * 60 * 24);
  return days < DISMISS_COOLDOWN_DAYS;
};

const InstallAppPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [showIosBanner, setShowIosBanner] = useState(false);

  useEffect(() => {
    if (isInStandaloneMode() || wasRecentlyDismissed()) return;

    const onBeforeInstallPrompt = (e) => {
      // Stop Chrome's own mini-infobar - we'll trigger the prompt ourselves.
      e.preventDefault();
      setDeferredPrompt(e);
      setShowBanner(true);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);

    // If the browser already installed it while we were listening.
    const onInstalled = () => {
      setShowBanner(false);
      setDeferredPrompt(null);
    };
    window.addEventListener('appinstalled', onInstalled);

    // iOS Safari: no event to listen for, just show manual instructions.
    if (isIos()) {
      setShowIosBanner(true);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setShowBanner(false);
    setShowIosBanner(false);
  };

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    // outcome is 'accepted' or 'dismissed' - either way the prompt is spent
    // and can't be reused, so clear it regardless.
    setDeferredPrompt(null);
    setShowBanner(false);
    if (outcome === 'dismissed') {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    }
  };

  if (showBanner) {
    return (
      <div className="install-app-banner" role="dialog" aria-label={tr("Install app")}>
        <div className="install-app-icon">
          <i className="fas fa-mobile-alt" />
        </div>
        <div className="install-app-text">
          <div className="install-app-title">{tr("Install AK4tek FMS")}</div>
          <div className="install-app-subtitle">{tr("Add it to your home screen for the full app experience.")}</div>
        </div>
        <div className="install-app-actions">
          <button className="install-app-btn" onClick={handleInstallClick}>{tr("Install")}</button>
          <button className="install-app-dismiss" onClick={dismiss} aria-label={tr("Dismiss")}>
            <i className="fas fa-times" />
          </button>
        </div>
      </div>
    );
  }

  if (showIosBanner) {
    return (
      <div className="install-app-banner install-app-banner--ios" role="dialog" aria-label={tr("Install app")}>
        <div className="install-app-icon">
          <i className="fas fa-mobile-alt" />
        </div>
        <div className="install-app-text">
          <div className="install-app-title">{tr("Install AK4tek FMS")}</div>
          <div className="install-app-subtitle">
            {tr("Tap")} <i className="fas fa-share-square" /> {tr("Share, then \"Add to Home Screen\".")}
          </div>
        </div>
        <div className="install-app-actions">
          <button className="install-app-dismiss" onClick={dismiss} aria-label={tr("Dismiss")}>
            <i className="fas fa-times" />
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default InstallAppPrompt;